import { MinMaxGradeRange, MinMaxGradingScale, SettingsSection, SettingsSubSection } from "p6m-p6u";
import CounterControl from "../../../../../../basic/counterControl/CounterControl";
import { formatScoreToCommaSeparated } from "../../../../../../helpers/ScoringHelper";
import { ChangeSettingsParams } from "../../../PDFConfiguration";
import { UpdateGradingScaleParams } from "./PDFGradingScaleEditor";
import { GradeRangeLimitType, GradingScaleFormat } from "../../../../../../enums/settings";
import { EditScoreDirection } from "../../../../../../enums/directions";

import {
    GradeRangeText,
    GradeRangeWrapper,
    GradingScaleEditorCell,
    ScoreAndCounterControlWrapper,
    Text,
} from "./StyledComponents";

interface PDFGradeRangeEditorProps {
    gradingScale: MinMaxGradingScale;
    gradingScaleFormat: GradingScaleFormat;
    gradeRange: MinMaxGradeRange;
    index: number;
    overallScore: number;
    adaptableScoreLeftIndex: number;
    adaptableScoreRightIndex: number;
    updateGradeRange: (updateGradingScaleParams: UpdateGradingScaleParams) => void;
    changeSettings: <T extends SettingsSection, K extends SettingsSubSection<T>>(
        changeSettingsParams: ChangeSettingsParams<T, K>
    ) => void;
}

const PDFGradeRangeEditor = (props: PDFGradeRangeEditorProps) => {
    const isRangeRedcutionPossible = props.gradeRange.maxScore !== props.gradeRange.minScore;

    const isAdaptableScoreLeft = props.adaptableScoreLeftIndex >= 0;
    const isAdaptableScoreRight = props.adaptableScoreRightIndex >= 0;

    const updateGradeRange = (minOrMax: GradeRangeLimitType, direction: EditScoreDirection) => {
        props.updateGradeRange({ gradingScale: props.gradingScale, index: props.index, minOrMax, direction });
    };

    const renderScoreAndControl = (minOrMax: GradeRangeLimitType, showControl: boolean) => {
        const isMin = minOrMax === GradeRangeLimitType.MIN;
        const score = isMin ? props.gradeRange.minScore : props.gradeRange.maxScore;
        const isDecreasable = isMin ? isAdaptableScoreRight : isRangeRedcutionPossible;
        const isIncreasable = isMin ? isRangeRedcutionPossible : isAdaptableScoreLeft;

        const gradeRangeFormatSuffix = props.gradingScaleFormat === GradingScaleFormat.PERCENTAGE ? " %" : "";
        const formattedNumber =
            props.gradingScaleFormat === GradingScaleFormat.PERCENTAGE ? score : formatScoreToCommaSeparated(score);

        return (
            <ScoreAndCounterControlWrapper isMin={isMin}>
                <GradeRangeText isMin={isMin}>{formattedNumber + gradeRangeFormatSuffix}</GradeRangeText>
                {showControl && (
                    <CounterControl
                        currentCount={score}
                        onIncrement={() => updateGradeRange(minOrMax, EditScoreDirection.INCREMENT)}
                        onDecrement={() => updateGradeRange(minOrMax, EditScoreDirection.DECREMENT)}
                        minPoints={isDecreasable ? 0 : score}
                        maxPoints={isIncreasable ? props.overallScore : score}
                    />
                )}
            </ScoreAndCounterControlWrapper>
        );
    };

    return (
        <GradingScaleEditorCell stretch>
            <GradeRangeWrapper>
                {renderScoreAndControl(GradeRangeLimitType.MAX, props.index > 0)}
                <Text>{"—"}</Text>
                {renderScoreAndControl(GradeRangeLimitType.MIN, props.index < props.gradingScale.length - 1)}
            </GradeRangeWrapper>
        </GradingScaleEditorCell>
    );
};

export default PDFGradeRangeEditor;
